import React, { Children } from 'react';
import CloseButton, { CloseButtonProps } from '../CloseButton';
import TextLabel from './components/TextLabel';
import ActionButton from './components/ActionButton';
import { st, classes } from './Notification.st.css';
import {
  StatusComplete,
  StatusWarning,
  StatusAlert,
} from '@wix/wix-ui-icons-common';
import { dataHooks } from './Notification.constants';
import { timingMap } from '../Transition/constants';
import deprecationLog from '../utils/deprecationLog';

import type {
  MappedChildren,
  NotificationProps,
  NotificationSkin,
  NotificationState,
} from './Notification.types';

export const LOCAL_NOTIFICATION = 'local';
export const GLOBAL_NOTIFICATION = 'global';
export const STICKY_NOTIFICATION = 'sticky';
export const DEFAULT_AUTO_HIDE_TIMEOUT = 6000;
export const DEFAULT_TIMEOUT = DEFAULT_AUTO_HIDE_TIMEOUT;

const animationsTimeouts = {
  enter: 300,
  exit: 300,
};

const skinIcon: Partial<Record<NotificationSkin, React.ReactElement>> = {
  error: <StatusAlert className={classes.iconStyling} />,
  success: <StatusComplete className={classes.iconStyling} />,
  warning: <StatusWarning className={classes.iconStyling} />,
};

function mapChildren(children: React.ReactNode): MappedChildren {
  const childrenArray = Children.toArray(children);
  return childrenArray.reduce<MappedChildren>((childrenAsMap, child) => {
    if (!React.isValidElement(child)) return childrenAsMap;
    const displayName = (child.type as React.FC).displayName;
    switch (displayName) {
      case 'Notification.TextLabel':
        childrenAsMap.label = child;
        break;
      case 'Notification.ActionButton':
        childrenAsMap.ctaButton = child;
        break;
      case 'Notification.CloseButton':
        childrenAsMap.closeButton = React.cloneElement(child, {
          size: 'small',
        });
        break;
      default:
        break;
    }
    return childrenAsMap;
  }, {});
}

class Notification extends React.PureComponent<
  NotificationProps,
  NotificationState
> {
  static displayName = 'Notification';
  static CloseButton: React.FC<CloseButtonProps>;
  static TextLabel: typeof TextLabel;
  static ActionButton: typeof ActionButton;

  static defaultProps: Partial<NotificationProps> = {
    theme: 'standard', // TODO: add default value for skin once theme is removed
    type: GLOBAL_NOTIFICATION,
  };

  closeTimeout: ReturnType<typeof setTimeout> | null = null;
  transitionTimeout: ReturnType<typeof setTimeout> | null = null;
  ref = React.createRef<HTMLDivElement>();
  childRef = React.createRef<HTMLDivElement>();

  state: NotificationState = {
    hideByCloseClick: false,
    hideByTimer: false,
    height: 0,
    showChildren: false,
  };

  UNSAFE_componentWillReceiveProps(nextProps: NotificationProps) {
    if (nextProps.show) {
      this._setChildrenVisibility(nextProps);
      this._bypassCloseFlags();
      this._clearCloseTimeout();
      this._startCloseTimer(nextProps);
    }
  }

  componentDidMount() {
    this._setChildrenVisibility(this.props);
    this._startCloseTimer(this.props);
  }

  componentWillUnmount() {
    this._clearCloseTimeout();
    this._clearTransitionTimeout();
  }

  componentDidUpdate(
    prevProps: NotificationProps,
    prevState: NotificationState,
  ) {
    const { showChildren, height, hideByCloseClick, hideByTimer } = this.state;
    if (
      prevProps.show !== this.props.show ||
      prevState.showChildren !== this.state.showChildren
    ) {
      const show = this._shouldShowNotification(this.props.show);
      if (show) {
        if (showChildren && this.ref.current && height === 0) {
          this.setState({ height: this._setHeightToChild(this.ref.current) });
        }
      } else {
        if (showChildren && height !== 0) {
          this.setState({ height: 0 });
        }
      }
    }

    if (
      prevState.hideByCloseClick !== hideByCloseClick ||
      prevState.hideByTimer !== hideByTimer
    ) {
      this._setChildrenVisibility(this.props);
    }
  }

  _setChildrenVisibility({ show }: NotificationProps) {
    this._clearTransitionTimeout();
    if (this._shouldShowNotification(show)) {
      this.setState({ showChildren: true });
    } else {
      this.setState({ height: 0 });
      this.transitionTimeout = setTimeout(() => {
        this.setState({ showChildren: false });
        this.transitionTimeout = null;
      }, timingMap['medium01']);
    }
  }

  _clearTransitionTimeout() {
    if (this.transitionTimeout) {
      clearTimeout(this.transitionTimeout);
      this.transitionTimeout = null;
    }
  }

  _startCloseTimer({ autoHideTimeout }: NotificationProps) {
    if (autoHideTimeout) {
      this.closeTimeout = setTimeout(
        () => this._hideNotificationOnTimeout(),
        autoHideTimeout || DEFAULT_AUTO_HIDE_TIMEOUT,
      );
    }
  }

  _clearCloseTimeout() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  _hideNotificationOnCloseClick = () => {
    this.setState({ hideByCloseClick: true });

    setTimeout(
      () => this.props.onClose && this.props.onClose('hide-by-close-click'),
      animationsTimeouts.exit + 100,
    );
  };

  _hideNotificationOnTimeout = () => {
    this.setState({ hideByTimer: true });

    setTimeout(
      () => this.props.onClose && this.props.onClose('hide-by-timer'),
      animationsTimeouts.exit + 100,
    );
  };

  _bypassCloseFlags() {
    this.setState({
      hideByCloseClick: false,
      hideByTimer: false,
    });
  }

  _shouldShowNotification(show?: boolean) {
    return show && !this.state.hideByCloseClick && !this.state.hideByTimer;
  }

  _setHeightToChild(node: HTMLDivElement) {
    const height =
      this.childRef && this.childRef.current
        ? this.childRef.current.offsetHeight
        : 0;

    if (node && node.firstChild) {
      (node.firstChild as HTMLElement).style.height = `${height}px`;
    }

    return height;
  }

  _getChildren() {
    if (!this.state.showChildren) {
      return null;
    }

    const { theme, children } = this.props;
    const skin = this.props.skin || theme;
    const childrenComponents = mapChildren(children);
    const icon = skin ? skinIcon[skin] : undefined;

    return (
      <div
        className={classes.wrapper}
        style={{
          overflow: 'hidden',
          transition: `height ${timingMap['medium01']}ms`,
          height: this.state.height,
        }}
      >
        <div
          data-hook={dataHooks.notificationContent}
          className={classes.notification}
          role="alert"
          aria-labelledby="notification-label"
          aria-live="polite"
          ref={this.childRef}
        >
          {icon && <div>{icon}</div>}

          <div className={classes.labelWrapper}>
            {childrenComponents.label}
            {childrenComponents.ctaButton}
          </div>

          {childrenComponents.closeButton && (
            <div
              data-hook={dataHooks.notificationCloseButton}
              className={classes.closeButton}
              onClick={this._hideNotificationOnCloseClick}
              children={childrenComponents.closeButton}
            />
          )}
        </div>
      </div>
    );
  }

  render() {
    const { dataHook, theme, type, zIndex } = this.props;
    const skin = this.props.skin || theme;

    if (theme) {
      deprecationLog(
        '<Notification/> - theme prop is deprecated and will be removed in next major version, please use skin instead',
      );
    }

    return (
      <div
        className={st(classes.root, { skin, type })}
        style={{ zIndex }}
        data-hook={dataHook}
        data-skin={skin}
        data-type={type}
      >
        <div
          className={classes.animator}
          style={{
            overflow: 'hidden',
            transition: `height ${timingMap['medium01']}ms`,
            height: this.state.height,
          }}
        >
          <div ref={this.ref} className={classes.animatorContent}>
            {this._getChildren()}
          </div>
        </div>
      </div>
    );
  }
}

const Close: React.FC<CloseButtonProps> = props => (
  <CloseButton skin="lightFilled" {...props} />
);

Close.displayName = 'Notification.CloseButton';

Notification.CloseButton = Close;
Notification.TextLabel = TextLabel;
Notification.ActionButton = ActionButton;

export default Notification;
